import React, { useEffect, useState } from 'react';
import { Tabs, Form, Input, InputNumber, Button, Typography, App, Switch, Table, Space, Select, Modal, Card, Collapse, Badge, Descriptions, Upload, message as antMessage } from 'antd';
import { SaveOutlined, ApiOutlined, MailOutlined, GiftOutlined, SettingOutlined, CreditCardOutlined, CrownOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import api from '@/api/client';

const { Title, Text } = Typography;
const { Panel } = Collapse;

// JSON 配置项的字段说明
const JSON_FIELD_DESCRIPTIONS: Record<string, Record<string, string>> = {
  'invite_settings': {
    'register_reward': '新用户注册奖励积分',
    'recharge_reward_percent': '邀请人获得被邀请人充值金额的百分比奖励',
    'lv3_reward': '达到3级邀请关系的额外奖励',
    'milestone_10': '邀请满10人的里程碑奖励',
    'milestone_50': '邀请满50人的里程碑奖励',
    'quota_enabled': '是否启用邀请配额制度',
    'free_quota': '免费用户的邀请配额上限',
    'basic_quota': '基础用户的邀请配额上限',
    'vip_quota': 'VIP用户的邀请配额上限',
  },
  'coach_settings': {
    'verification_reward': '完成教练认证奖励积分',
    'monthly_reward': '每月活跃奖励积分',
    'discount_percent': '教练服务折扣百分比',
  },
  'payment_settings': {
    'order_expire_minutes': '订单过期时间（分钟）',
    'max_pending_orders': '用户最大待支付订单数',
  },
  'identity_credits': {
    'coach': '产品教练身份注册赠送积分',
    'super_pm': '超级产品经理身份注册赠送积分',
    'angel_pm': '天使产品身份注册赠送积分',
  },
  'ref_image_compress': {
    'preset': '压缩预设，可选值：low/medium/high/original/custom',
    'customMaxWidth': '自定义压缩最大宽度（像素）',
    'customMaxHeight': '自定义压缩最大高度（像素）',
    'customQuality': '自定义压缩质量（1-100）',
    'customFormat': '自定义输出格式，如 webp、jpeg、png',
    'customMaxSize': '自定义最大文件大小（字节）',
  },
};

// 配置分类
const CONFIG_CATEGORIES = [
  {
    key: 'ai',
    name: 'AI 模型配置',
    icon: <ApiOutlined />,
    color: '#1677ff',
    keys: ['openai_api_key', 'openai_base_url', 'image_model', 'text_model', 'vision_model', 'text_api_key'],
  },
  {
    key: 'email',
    name: '邮件服务配置',
    icon: <MailOutlined />,
    color: '#52c41a',
    keys: ['email_service', 'email_user', 'email_pass', 'email_from_name'],
  },
  {
    key: 'reward',
    name: '积分奖励配置',
    icon: <GiftOutlined />,
    color: '#faad14',
    keys: ['invite_settings', 'coach_settings'],
  },
  {
    key: 'payment',
    name: '支付订单配置',
    icon: <CreditCardOutlined />,
    color: '#722ed1',
    keys: ['payment_settings'],
  },
  {
    key: 'identity',
    name: '身份积分配置',
    icon: <CrownOutlined />,
    color: '#ff6b9d',
    keys: ['identity_credits'],
  },
  {
    key: 'other',
    name: '其他配置',
    icon: <SettingOutlined />,
    color: '#8c8c8c',
    keys: [],
  },
];

// 配置项说明映射
const CONFIG_DESCRIPTIONS: Record<string, { label: string; desc: string; category?: string }> = {
  'openai_api_key': {
    label: 'OpenAI API Key',
    desc: '用于调用 OpenAI 图像生成服务的 API 密钥',
    category: 'ai',
  },
  'openai_base_url': {
    label: 'OpenAI API 基础 URL',
    desc: 'OpenAI 兼容 API 的基础地址',
    category: 'ai',
  },
  'image_model': {
    label: '图像生成模型',
    desc: '用于生成图像的 AI 模型名称',
    category: 'ai',
  },
  'text_model': {
    label: '文本对话模型',
    desc: '用于对话和文本生成的 AI 模型',
    category: 'ai',
  },
  'vision_model': {
    label: '图像理解模型',
    desc: '用于分析图像内容的 AI 模型',
    category: 'ai',
  },
  'text_api_key': {
    label: '文本 API Key',
    desc: '用于调用文本对话服务的 API 密钥（可与图像 API 不同）',
    category: 'ai',
  },
  'email_service': {
    label: '邮件服务类型',
    desc: '使用的邮件服务提供商，如 qq、gmail、outlook',
    category: 'email',
  },
  'email_user': {
    label: '邮件账号',
    desc: '发送邮件的邮箱地址',
    category: 'email',
  },
  'email_pass': {
    label: '邮件授权码',
    desc: '邮箱的 SMTP 授权码（不是登录密码）',
    category: 'email',
  },
  'email_from_name': {
    label: '发件人名称',
    desc: '邮件中显示的发件人名字',
    category: 'email',
  },
  'invite_settings': {
    label: '邀请设置',
    desc: '用户邀请系统的积分奖励配置',
    category: 'reward',
  },
  'coach_settings': {
    label: '教练功能设置',
    desc: 'AI 教练功能的积分奖励和折扣配置',
    category: 'reward',
  },
  'payment_settings': {
    label: '支付设置',
    desc: '订单超时和并发限制配置',
    category: 'payment',
  },
  'identity_credits': {
    label: '身份积分配置',
    desc: '不同身份等级用户注册时赠送的初始积分',
    category: 'identity',
  },
  'wechat_group_qrcode': {
    label: '微信群二维码',
    desc: '注册页面显示的微信群二维码图片 URL',
  },
  'wechat_id_qrcode': {
    label: '微信号二维码',
    desc: '注册页面显示的微信号二维码图片 URL',
  },
  'ref_image_compress': {
    label: '参考图压缩配置',
    desc: '参考图上传时的压缩参数配置',
  },
};

// 获取配置项的分类
const getConfigCategory = (key: string): string => {
  return CONFIG_DESCRIPTIONS[key]?.category || 'other';
};

// 格式化 JSON 字符串
const formatJson = (str: string): string => {
  try {
    const obj = JSON.parse(str);
    return JSON.stringify(obj, null, 2);
  } catch {
    return str;
  }
};

// 解析 JSON 配置并渲染字段说明
const renderJsonFieldDescriptions = (configKey: string, jsonValue: string) => {
  const fieldDescs = JSON_FIELD_DESCRIPTIONS[configKey];
  if (!fieldDescs) return null;

  try {
    const obj = JSON.parse(jsonValue);
    const fields = Object.keys(obj);
    
    if (fields.length === 0) return null;

    return (
      <Descriptions size="small" column={1} style={{ marginTop: 8, background: '#f6ffed', padding: 12, borderRadius: 4 }}>
        {fields.map(field => (
          <Descriptions.Item key={field} label={field}>
            <Text type="secondary">{fieldDescs[field] || '暂无说明'}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
  } catch {
    return null;
  }
};

export default function SystemPage() {
  const [sysConfig, setSysConfig] = useState<any[]>([]);
  const [taskConfig, setTaskConfig] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [sysForm] = Form.useForm();
  const [pkgForm] = Form.useForm();
  const [pkgModal, setPkgModal] = useState<{ open: boolean; editData?: any }>({ open: false });
  const { message } = App.useApp();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sys, task, pkg] = await Promise.all([
        api.get('/system-config'),
        api.get('/task-config'),
        api.get('/packages'),
      ]);
      setSysConfig(sys.data.data || sys.data || []);
      setTaskConfig(task.data.data || task.data || []);
      setPackages(pkg.data.data || pkg.data || []);
      setEditingValues({});
    } catch { message.error('加载配置失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleSysSave = async (record: any) => {
    try {
      await api.put(`/system-config/${record.config_key}`, { value: record.config_value });
      message.success('保存成功');
    } catch { message.error('保存失败'); }
  };

  // 处理图片上传
  const handleImageUpload = async (file: File, configKey: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'config');

    try {
      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setEditingValues({ ...editingValues, [configKey]: res.data.url });
        antMessage.success('上传成功');
        return res.data.url;
      }
    } catch (err) {
      antMessage.error('上传失败');
    }
    return null;
  };

  // 渲染图片上传组件
  const renderImageUpload = (configKey: string, value: string) => {
    const isImageConfig = configKey === 'wechat_group_qrcode' || configKey === 'wechat_id_qrcode';
    if (!isImageConfig) return null;

    return (
      <div style={{ marginTop: 8 }}>
        {value && (
          <div style={{ marginBottom: 8 }}>
            <img
              src={value}
              alt="预览"
              style={{ maxWidth: 120, maxHeight: 120, borderRadius: 4, border: '1px solid #d9d9d9' }}
            />
          </div>
        )}
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={(file) => {
            handleImageUpload(file, configKey);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />} size="small">
            {value ? '重新上传' : '上传图片'}
          </Button>
        </Upload>
      </div>
    );
  };

  const handleTaskSave = async (record: any) => {
    try {
      await api.put(`/task-config/${record.task_type}`, record);
      message.success('保存成功');
    } catch { message.error('保存失败'); }
  };

  const handlePkgSubmit = async () => {
    const values = await pkgForm.validateFields();
    try {
      if (pkgModal.editData) {
        await api.put(`/packages/${pkgModal.editData.id}`, values);
      } else {
        await api.post('/packages', values);
      }
      message.success(pkgModal.editData ? '更新成功' : '创建成功');
      setPkgModal({ open: false });
      pkgForm.resetFields();
      loadAll();
    } catch { message.error('操作失败'); }
  };

  const sysColumns = [
    {
      title: '配置项',
      dataIndex: 'config_key',
      width: 200,
      render: (key: string) => {
        const config = CONFIG_DESCRIPTIONS[key];
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{config?.label || key}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{key}</Text>
          </div>
        );
      },
    },
    {
      title: '配置值', dataIndex: 'config_value', width: 400,
      render: (v: string, r: any) => {
        const editValue = editingValues[r.config_key] ?? v;
        const isJson = (() => { try { JSON.parse(v); return true; } catch { return false; } })();
        const displayValue = isJson ? formatJson(editValue) : editValue;
        const isImageConfig = r.config_key === 'wechat_group_qrcode' || r.config_key === 'wechat_id_qrcode';

        return (
          <div>
            <Input.TextArea
              value={displayValue}
              onChange={(e) => setEditingValues({ ...editingValues, [r.config_key]: e.target.value })}
              autoSize={{ minRows: isJson ? 6 : 1, maxRows: 10 }}
              style={{ fontFamily: isJson ? 'monospace' : 'inherit', fontSize: 12 }}
            />
            {isJson && renderJsonFieldDescriptions(r.config_key, editValue)}
            {isImageConfig && renderImageUpload(r.config_key, editValue)}
          </div>
        );
      },
    },
    {
      title: '说明', width: 280,
      render: (_: any, r: any) => {
        const config = CONFIG_DESCRIPTIONS[r.config_key];
        if (!config) return <span style={{ color: '#999' }}>-</span>;
        return (
          <div style={{ color: '#666' }}>
            {config.desc}
          </div>
        );
      },
    },
    {
      title: '操作', width: 100, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          onClick={() => handleSysSave({ ...r, config_value: editingValues[r.config_key] ?? r.config_value })}
        >
          保存
        </Button>
      ),
    },
  ];

  const taskColumns = [
    { title: '任务类型', dataIndex: 'task_type', width: 140 },
    { title: '名称', dataIndex: 'task_name', width: 120 },
    { title: '积分', dataIndex: 'reward_credits', width: 70 },
    {
      title: '可重复', dataIndex: 'is_repeatable', width: 70,
      render: (v: any, r: any) => <Switch size="small" checked={!!v} onChange={(c) => { r.is_repeatable = c ? 1 : 0; setTaskConfig([...taskConfig]); }} />,
    },
    {
      title: '启用', dataIndex: 'is_enabled', width: 70,
      render: (v: any, r: any) => <Switch size="small" checked={!!v} onChange={(c) => { r.is_enabled = c ? 1 : 0; setTaskConfig([...taskConfig]); }} />,
    },
    {
      title: '操作', width: 80,
      render: (_: any, r: any) => <Button size="small" icon={<SaveOutlined />} onClick={() => handleTaskSave(r)}>保存</Button>,
    },
  ];

  const pkgColumns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: '名称', dataIndex: 'name' },
    { title: '积分', dataIndex: 'credits', width: 80 },
    { title: '价格', dataIndex: 'amount', width: 80, render: (v: number) => `¥${v}` },
    {
      title: '操作', width: 120,
      render: (_: any, r: any) => (
        <Space>
          <Button size="small" onClick={() => { pkgForm.setFieldsValue(r); setPkgModal({ open: true, editData: r }); }}>编辑</Button>
          <Button size="small" danger onClick={async () => { await api.delete(`/packages/${r.id}`); loadAll(); }}>删除</Button>
        </Space>
      ),
    },
  ];

  // 默认配置值
  const DEFAULT_CONFIG_VALUES: Record<string, string> = {
    'identity_credits': JSON.stringify({ coach: 300, super_pm: 500, angel_pm: 200 }, null, 2),
  };

  // 按分类渲染配置表格
  const renderConfigByCategory = (category: typeof CONFIG_CATEGORIES[0]) => {
    // 获取数据库中存在的配置
    const existingConfigs = sysConfig.filter(item => 
      category.keys.includes(item.config_key) || 
      (category.key === 'other' && !CONFIG_DESCRIPTIONS[item.config_key]?.category)
    );
    
    // 对于预定义的配置项，即使数据库中不存在也要显示
    let categoryConfigs;
    if (category.key === 'other') {
      // 其他配置：包含所有没有 category 的预定义配置项 + 数据库中未定义的其他配置
      const predefinedOtherKeys = Object.keys(CONFIG_DESCRIPTIONS).filter(
        key => !CONFIG_DESCRIPTIONS[key]?.category
      );
      const allOtherKeys = new Set([
        ...predefinedOtherKeys,
        ...existingConfigs.map(item => item.config_key)
      ]);
      categoryConfigs = Array.from(allOtherKeys).map(key => {
        const existing = existingConfigs.find(item => item.config_key === key);
        if (existing) return existing;
        return {
          config_key: key,
          config_value: DEFAULT_CONFIG_VALUES[key] || '',
          description: CONFIG_DESCRIPTIONS[key]?.desc || '',
          isNew: true,
        };
      });
    } else {
      categoryConfigs = category.keys.map(key => {
        const existing = existingConfigs.find(item => item.config_key === key);
        if (existing) return existing;
        return {
          config_key: key,
          config_value: DEFAULT_CONFIG_VALUES[key] || '',
          description: CONFIG_DESCRIPTIONS[key]?.desc || '',
          isNew: true,
        };
      });
    }
    
    if (categoryConfigs.length === 0 && category.key !== 'other') return null;
    
    return (
      <Panel 
        header={
          <Space>
            <span style={{ color: category.color }}>{category.icon}</span>
            <span style={{ fontWeight: 500 }}>{category.name}</span>
            <Badge count={categoryConfigs.length} style={{ backgroundColor: category.color }} />
          </Space>
        } 
        key={category.key}
      >
        <Table 
          columns={sysColumns} 
          dataSource={categoryConfigs} 
          rowKey="config_key" 
          pagination={false} 
          size="small" 
          loading={loading}
          showHeader={false}
        />
      </Panel>
    );
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>系统设置</Title>
      <Tabs items={[
        {
          key: 'systems', label: '系统配置',
          children: (
            <Collapse defaultActiveKey={['ai', 'email', 'reward', 'payment', 'identity']}>
              {CONFIG_CATEGORIES.map(cat => renderConfigByCategory(cat))}
            </Collapse>
          ),
        },
        {
          key: 'tasks', label: '任务配置',
          children: <Table columns={taskColumns} dataSource={taskConfig} rowKey="task_type" pagination={false} size="small" loading={loading} />,
        },
        {
          key: 'packages', label: '充值套餐',
          children: (
            <>
              <Space style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={() => { pkgForm.resetFields(); setPkgModal({ open: true }); }}>新增套餐</Button>
              </Space>
              <Table columns={pkgColumns} dataSource={packages} rowKey="id" pagination={false} size="small" loading={loading} />
              <Modal title={pkgModal.editData ? '编辑套餐' : '新增套餐'} open={pkgModal.open} onCancel={() => setPkgModal({ open: false })} onOk={handlePkgSubmit}>
                <Form form={pkgForm} layout="vertical">
                  <Form.Item name="name" label="套餐名称" rules={[{ required: true }]}><Input /></Form.Item>
                  <Form.Item name="credits" label="积分" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
                  <Form.Item name="price" label="价格(元)" rules={[{ required: true }]}><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
                  <Form.Item name="description" label="描述"><Input.TextArea rows={2} /></Form.Item>
                </Form>
              </Modal>
            </>
          ),
        },
      ]} />
    </div>
  );
}
